/**
 * Standard Error Format Utility
 *
 * REFACTORED: Now creates throwable Error objects instead of plain objects
 * Services should THROW errors, not return error objects
 *
 * Provides consistent error object format for all services.
 * Based on the pattern established in MarketDataService.js.
 */

/**
 * Error types for all operations
 */
export const ErrorType = {
  DATA_UNAVAILABLE: 'DATA_UNAVAILABLE',
  NETWORK_ERROR: 'NETWORK_ERROR',
  PARSE_ERROR: 'PARSE_ERROR',
  TIMEOUT_ERROR: 'TIMEOUT_ERROR',
  CACHE_ERROR: 'CACHE_ERROR',
  VALIDATION_ERROR: 'VALIDATION_ERROR'
};

/**
 * Custom Error class that includes errorType and additional metadata
 */
export class ServiceError extends Error {
  constructor(message, errorType, originalError = null) {
    super(message);
    this.name = 'ServiceError';
    this.errorType = errorType;
    this.timestamp = new Date().toISOString();

    if (originalError) {
      this.originalError = originalError;

      // Add debug info in development
      if (process.env.NODE_ENV === 'development') {
        this.debug = {
          originalMessage: originalError.message,
          stack: originalError.stack
        };
      }
    }

    // Maintains proper stack trace for where error was thrown
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ServiceError);
    }
  }
}

/**
 * Create a standard error response object
 *
 * @param {Object} options - Error configuration
 * @param {string} options.errorType - Type of error from ErrorType enum
 * @param {string} options.message - Human-readable error message
 * @param {Object} [options.fallbackData] - Optional fallback data to prevent crashes
 * @param {Error} [options.originalError] - Original error object for debugging
 * @returns {Object} Standard error format object
 */
export function createErrorResponse({
  errorType,
  message,
  fallbackData = null,
  originalError = null
}) {
  const errorResponse = {
    error: true,
    errorType,
    message,
    timestamp: new Date().toISOString()
  };

  // Add fallback data if provided
  if (fallbackData !== null) {
    errorResponse.fallbackData = fallbackData;
  }

  // Add debug info in development
  if (process.env.NODE_ENV === 'development' && originalError) {
    errorResponse.debug = {
      originalMessage: originalError.message,
      stack: originalError.stack
    };
  }

  return errorResponse;
}

/**
 * Check if an object is a standard error response
 *
 * @param {*} obj - Object to check
 * @returns {boolean} True if object is a standard error response
 */
export function isErrorResponse(obj) {
  return (
    obj !== null &&
    typeof obj === 'object' &&
    obj.error === true &&
    typeof obj.errorType === 'string' &&
    typeof obj.message === 'string' &&
    typeof obj.timestamp === 'string'
  );
}

/**
 * Create a data unavailable error (THROWS)
 * Common case when Lambda/S3 data is not accessible
 *
 * @param {string} message - Human-readable error message
 * @param {Object} [fallbackData] - Optional fallback data
 * @throws {ServiceError} Always throws
 */
export function createDataUnavailableError(message, fallbackData = null) {
  const error = new ServiceError(message, ErrorType.DATA_UNAVAILABLE);
  if (fallbackData) {
    error.fallbackData = fallbackData;
  }
  throw error;
}

/**
 * Create a network error (THROWS)
 * Used when API calls fail due to network issues
 *
 * @param {string} message - Human-readable error message
 * @param {Error} [originalError] - Original network error
 * @throws {ServiceError} Always throws
 */
export function createNetworkError(message, originalError = null) {
  throw new ServiceError(message, ErrorType.NETWORK_ERROR, originalError);
}

/**
 * Create a parse error (THROWS)
 * Used when JSON parsing or data transformation fails
 *
 * @param {string} message - Human-readable error message
 * @param {Error} [originalError] - Original parse error
 * @throws {ServiceError} Always throws
 */
export function createParseError(message, originalError = null) {
  throw new ServiceError(message, ErrorType.PARSE_ERROR, originalError);
}

/**
 * Create a timeout error (THROWS)
 * Used when operations take too long
 *
 * @param {string} message - Human-readable error message
 * @throws {ServiceError} Always throws
 */
export function createTimeoutError(message) {
  throw new ServiceError(message, ErrorType.TIMEOUT_ERROR);
}

/**
 * Create a cache error (THROWS)
 * Used when cache operations fail
 *
 * @param {string} message - Human-readable error message
 * @param {Error} [originalError] - Original cache error
 * @throws {ServiceError} Always throws
 */
export function createCacheError(message, originalError = null) {
  throw new ServiceError(message, ErrorType.CACHE_ERROR, originalError);
}

/**
 * Create a validation error (THROWS)
 * Used when data doesn't meet expected format/schema
 *
 * @param {string} message - Human-readable error message
 * @param {Object} [validationDetails] - Details about validation failures
 * @throws {ServiceError} Always throws
 */
export function createValidationError(message, validationDetails = null) {
  const error = new ServiceError(message, ErrorType.VALIDATION_ERROR);
  if (validationDetails) {
    error.validationDetails = validationDetails;
  }
  throw error;
}

/**
 * Wrap a promise with standard error handling
 * Automatically converts thrown errors into standard error format
 *
 * @param {Promise} promise - Promise to wrap
 * @param {string} defaultMessage - Default error message if conversion fails
 * @returns {Promise<Object>} Promise that resolves to data or error response
 */
export async function wrapWithErrorHandling(promise, defaultMessage = 'Operation failed') {
  try {
    return await promise;
  } catch (error) {
    console.error('[StandardErrorFormat] Error caught:', error);

    // Determine error type based on error characteristics
    let errorType = ErrorType.NETWORK_ERROR; // Default

    if (error.name === 'SyntaxError') {
      errorType = ErrorType.PARSE_ERROR;
    } else if (error.message?.includes('timeout')) {
      errorType = ErrorType.TIMEOUT_ERROR;
    } else if (error.message?.includes('cache')) {
      errorType = ErrorType.CACHE_ERROR;
    } else if (error.message?.includes('unavailable') || error.message?.includes('not found')) {
      errorType = ErrorType.DATA_UNAVAILABLE;
    }

    return createErrorResponse({
      errorType,
      message: error.message || defaultMessage,
      originalError: error
    });
  }
}

/**
 * Get user-friendly message for error type
 *
 * @param {string} errorType - Error type from ErrorType enum
 * @returns {string} User-friendly message
 */
export function getUserFriendlyMessage(errorType) {
  const messages = {
    [ErrorType.DATA_UNAVAILABLE]: 'Market data is temporarily unavailable. Please try again in a few minutes.',
    [ErrorType.NETWORK_ERROR]: 'Network connection issue. Please check your internet connection and try again.',
    [ErrorType.PARSE_ERROR]: 'Data format error. Please contact support if this persists.',
    [ErrorType.TIMEOUT_ERROR]: 'Request timed out. Please try again.',
    [ErrorType.CACHE_ERROR]: 'Cache error. Data will be fetched directly.',
    [ErrorType.VALIDATION_ERROR]: 'Data validation failed. Please contact support.'
  };

  return messages[errorType] || 'An unexpected error occurred. Please try again.';
}

/**
 * Log error with standard format
 *
 * @param {Object} errorResponse - Standard error response object
 * @param {string} [context] - Optional context for debugging
 */
export function logError(errorResponse, context = '') {
  if (!isErrorResponse(errorResponse)) {
    console.error('[StandardErrorFormat] Invalid error response format:', errorResponse);
    return;
  }

  const prefix = context ? `[${context}]` : '[Error]';
  console.error(
    `${prefix} ${errorResponse.errorType}: ${errorResponse.message}`,
    {
      timestamp: errorResponse.timestamp,
      debug: errorResponse.debug,
      validationDetails: errorResponse.validationDetails
    }
  );
}
